// src/pages/LeadDetail.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getLead, updateLead, addTimeline, setFollowup, deleteLead } from '../utils/api';
import { StatusBadge, SourceBadge, formatDate, getTodayStr } from '../components/UI';
import LeadForm from '../components/LeadForm';
import { useAuth } from '../context/AuthContext';
import { useLeadLookups } from '../hooks/useLeadLookups';

const TL_ICONS = {
  'Lead Created': 'ti-user-plus', 'Auto Email Sent': 'ti-mail', 'Status Changed': 'ti-refresh',
  'Lead Updated': 'ti-edit', 'Follow-up Set': 'ti-calendar', 'Note Added': 'ti-message',
  'Lead Reassigned': 'ti-user-check',
  'Lead Imported': 'ti-file-import', 'Via Meta Instant Form': 'ti-brand-facebook',
};

export default function LeadDetail({ toast }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me, can } = useAuth();
  const { statuses, sources } = useLeadLookups();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [fuDate, setFuDate] = useState('');
  const [fuNote, setFuNote] = useState('');
  const [noteText, setNoteText] = useState('');
  const [savingFU, setSavingFU] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(() => {
    getLead(id)
      .then(r => { setLead(r.data); setFuDate(r.data.followup_date || ''); })
      .catch(() => navigate('/leads'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (status) => {
    try {
      await updateLead(id, { ...lead, status });
      toast('Status updated to ' + status);
      load();
    } catch { toast('Failed to update status'); }
  };

  const saveFollowup = async () => {
    setSavingFU(true);
    try {
      await setFollowup(id, { followup_date: fuDate, note: fuNote });
      toast('Follow-up date saved!');
      setFuNote('');
      load();
    } catch { toast('Failed to save follow-up'); } finally { setSavingFU(false); }
  };

  const saveNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await addTimeline(id, { event_title: 'Note Added', event_note: noteText.trim(), append_note: true });
      toast('Note added!');
      setNoteText('');
      load();
    } catch { toast('Failed to add note'); } finally { setSavingNote(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete lead "${lead?.name}"? This cannot be undone.`)) return;
    try { await deleteLead(id); navigate('/leads'); } catch { toast('Delete failed'); }
  };

  if (loading) return <div style={{ padding: 40, color: 'var(--gray-400)' }}>Loading...</div>;
  if (!lead) return null;

  const today = getTodayStr();
  const fuClass = lead.followup_date ? (lead.followup_date < today ? 'var(--red)' : lead.followup_date === today ? 'var(--amber)' : 'var(--gray-600)') : 'var(--gray-400)';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/leads')}>
          <i className="ti ti-arrow-left" />
        </button>
        <div style={{ fontSize: 16, fontWeight: 700 }}>{lead.name}</div>
        <StatusBadge status={lead.status} statuses={statuses} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {can('leads.edit') && (
            <button className="btn btn-outline btn-sm" onClick={() => setShowForm(true)}><i className="ti ti-edit" /> Edit</button>
          )}
          {can('leads.delete') && (
            <button className="btn btn-danger btn-sm" onClick={handleDelete}><i className="ti ti-trash" /></button>
          )}
        </div>
      </div>

      <div className="detail-grid">
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
              {[
                ['Phone', lead.phone || '—'],
                ['Email', lead.email || '—'],
                ['Company', lead.company || '—'],
                ['Service', lead.service || '—'],
                ['Source', <SourceBadge source={lead.source} sources={sources} />],
                ['Assigned To', lead.assigned_name || '— Unassigned —'],
                ['Added', formatDate(lead.added_date)],
              ].map(([label, value]) => (
                <div key={label} className="detail-field">
                  <div className="detail-field-label">{label}</div>
                  <div className="detail-field-value">{value}</div>
                </div>
              ))}
              {lead.form_name && (
                <div className="detail-field">
                  <div className="detail-field-label">Form</div>
                  <div className="detail-field-value" style={{ fontSize: 12, color: 'var(--gray-500)' }}>{lead.form_name}</div>
                </div>
              )}
            </div>
            <div className="detail-field">
              <div className="detail-field-label">Next Follow-up</div>
              <div className="detail-field-value" style={{ color: fuClass }}>{formatDate(lead.followup_date)}</div>
            </div>
            {lead.notes && (
              <div className="detail-field" style={{ marginTop: 12 }}>
                <div className="detail-field-label">Notes</div>
                <div style={{ fontSize: 13, color: 'var(--gray-600)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{lead.notes}</div>
              </div>
            )}
          </div>

          {(() => {
            let cf = {};
            try { cf = JSON.parse(lead.custom_fields || 'null') || {}; } catch {}
            const entries = Object.entries(cf);
            if (!entries.length) return null;
            return (
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-title">Form Data</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                  {entries.map(([k, v]) => (
                    <div key={k} className="detail-field" style={{ marginBottom: 2 }}>
                      <div className="detail-field-label" style={{ fontSize: 10 }}>{k}</div>
                      <div className="detail-field-value" style={{ fontSize: 13 }}>{String(v)}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {can('leads.edit') && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title">Add Note</div>
            <textarea className="form-input" value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note or update..." style={{ marginBottom: 8 }} />
            <button className="btn btn-primary btn-sm" onClick={saveNote} disabled={savingNote || !noteText.trim()}>
              {savingNote ? 'Saving...' : 'Add Note'}
            </button>
          </div>
          )}

          <div className="card">
            <div className="card-title">Activity Timeline</div>
            <div className="timeline">
              {(lead.timeline || []).map((e, i) => (
                <div key={i} className={`tl-item${i < lead.timeline.length - 1 ? ' tl-line' : ''}`}>
                  <div className="tl-dot"><i className={`ti ${TL_ICONS[e.event_title] || 'ti-clock'}`} style={{ fontSize: 12 }} /></div>
                  <div>
                    <div className="tl-title">{e.event_title}</div>
                    <div className="tl-time">{formatDate(e.event_date)}{e.event_note ? ' · ' + e.event_note : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {can('leads.edit') && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title">Update Status</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {statuses.map(s => (
                <button key={s.id} className="btn btn-outline" onClick={() => updateStatus(s.name)} style={{ textAlign: 'left', padding: '10px 14px', fontWeight: lead.status === s.name ? 700 : 400, borderColor: lead.status === s.name ? 'var(--black)' : 'var(--gray-200)' }}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: s.color, marginRight: 8 }} />
                  {s.name}
                </button>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-title">Set Follow-up Date</div>
            <input type="date" className="form-input" value={fuDate} onChange={e => setFuDate(e.target.value)} style={{ marginBottom: 8 }} />
            <textarea className="form-input" value={fuNote} onChange={e => setFuNote(e.target.value)} placeholder="Follow-up note..." style={{ marginBottom: 8, minHeight: 60 }} />
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={saveFollowup} disabled={savingFU}>
              {savingFU ? 'Saving...' : 'Save Follow-up'}
            </button>
          </div>
        </div>
        )}
      </div>

      <LeadForm
        open={showForm}
        onClose={() => setShowForm(false)}
        lead={lead}
        onSaved={() => { setShowForm(false); load(); }}
        toast={toast}
      />
    </div>
  );
}
